import type { LicenseCriteria, Organisation, XeroInvoiceState } from '../../types'
import { formatDate } from './helpers'

interface SummaryProps {
  selectedOrg: Organisation | undefined
  linkedCriteria: LicenseCriteria
}

export function LicenseSummaryCard({
  selectedOrg,
  linkedCriteria,
}: SummaryProps): React.ReactElement {
  const validUntilDisplay = linkedCriteria.validUntil
    ? formatDate(linkedCriteria.validUntil)
    : 'Perpetual'
  return (
    <div className="rounded-lg border border-[#4e5e82]/15 bg-[#4e5e82]/5 p-4 dark:border-[#4e5e82] dark:bg-[#8a9bba]/10">
      <h3 className="mb-2 font-medium text-[#4e5e82] dark:text-[#8a9bba]">License Summary</h3>
      <div className="space-y-1 text-sm text-[#5c6e94] dark:text-[#8a9bba]">
        <p>
          <strong>Organisation:</strong> {selectedOrg?.name}
        </p>
        <p>
          <strong>Type:</strong> {linkedCriteria.accessTier}
        </p>
        <p>
          <strong>Seats:</strong> {linkedCriteria.seatsGranted}
        </p>
        <p>
          <strong>Valid:</strong> {formatDate(linkedCriteria.validFrom)} → {validUntilDisplay}
        </p>
      </div>
    </div>
  )
}

interface PaymentWarningProps {
  xeroInvoices: XeroInvoiceState[]
  selectedOrgId: string
}

export function PaymentGateRequiredWarning({
  xeroInvoices,
  selectedOrgId,
}: PaymentWarningProps): React.ReactElement {
  const hasInvoicesForOrg = xeroInvoices.some((entry) => entry.organisationId === selectedOrgId)
  const detail = hasInvoicesForOrg
    ? 'Existing invoices are not fully paid. Wait for payment to complete or update invoice status.'
    : 'Import a Xero invoice and link it to this organisation in the Activation tab.'
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
      <p className="text-sm text-amber-700 dark:text-amber-400">
        <strong>Payment Gate Required:</strong> {detail}
      </p>
    </div>
  )
}

interface LicenseWarningProps {
  licenseCriteria: LicenseCriteria[]
  selectedOrgId: string
}

function buildLicenseWarningDetail(input: LicenseWarningProps): string {
  const { licenseCriteria, selectedOrgId } = input
  const allOrgCriteria = licenseCriteria.filter((entry) => entry.organisationId === selectedOrgId)
  if (allOrgCriteria.length === 0) {
    return 'Create license criteria for this organisation in the License Criteria tab.'
  }
  const orphanedCount = allOrgCriteria.filter((entry) => entry.isOrphaned).length
  if (orphanedCount > 0) {
    return 'Link existing criteria to a paid invoice (edit criteria and select an invoice).'
  }
  const pendingCount = allOrgCriteria.filter((entry) => entry.status === 'pending').length
  if (pendingCount > 0) {
    return 'Verify the pending license criteria in the License Criteria tab.'
  }
  return 'Create verified license criteria with an invoice link.'
}

export function LicenseGateRequiredWarning(props: LicenseWarningProps): React.ReactElement {
  const detail = buildLicenseWarningDetail(props)
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
      <p className="text-sm text-amber-700 dark:text-amber-400">
        <strong>License Gate Required:</strong> {detail}
      </p>
    </div>
  )
}
